import { Component, inject, OnInit, OnDestroy, computed, signal, ViewChild, ElementRef, effect, untracked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { firstValueFrom } from 'rxjs';

import { UserFormFacade } from '../../../../../Facades/user-form.facade';
import { ListRolesFacade } from '../../../../../Facades/list-roles.facade';
import { ApiService } from '../../../../../Modules/Shared/Services/api.service';
import { SearchService } from '../../../../../Services/Intune/search.service';
import { ScopeItem } from '../../../../../Interfaces/api/users.interface';
import { PermissionScope } from '../../../../../Models/avyna/permission-scopes.model';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AvLoadingItemComponent } from '../../../../Shared/Components/av-loading-item/av-loading-item.component';
import { AvContentContainerComponent } from '../../../../Shared/Components/av-content-container/av-content-container.component';
import { MsgService } from '../../../../Shared/Services/msg.service';
import { AuthService } from '../../../../Shared/Services/auth.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { PERMISSIONS } from '../../../../../Enums/permission.enum';

@Component({
  selector: 'app-user-edit',
  imports: [COMMON_IMPORTS, AvLoadingItemComponent, AvContentContainerComponent],
  providers: [UserFormFacade, ListRolesFacade],
  templateUrl: './user-edit.component.html',
  styleUrl: './user-edit.component.scss',
})
export class UserEditComponent implements OnInit, OnDestroy {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _formBuilder = inject(FormBuilder);
  readonly facade = inject(UserFormFacade);
  private readonly _listRolesFacade = inject(ListRolesFacade);
  private readonly _apiService = inject(ApiService);
  private readonly _searchService = inject(SearchService);
  private readonly _msgService = inject(MsgService);
  private readonly _authService = inject(AuthService);

  permissions = PERMISSIONS;

  readonly currentUser = toSignal(this._authService.currentUser$, { initialValue: null });

  readonly filteredRoles = computed(() =>
    this._listRolesFacade.roles().filter(r => r.name?.toLowerCase() !== 'root')
  );
  readonly isRolesLoading = computed(() => this._listRolesFacade.isLoading());

  readonly permissionSearchTerms = signal<Record<number, string>>({});
  readonly permissionSearchResults = signal<Record<number, ScopeItem[]>>({});
  readonly permissionSearchLoading = signal<Record<number, boolean>>({});
  readonly permissionSelectedScopes = signal<Record<number, ScopeItem[]>>({});

  readonly isSaving = signal(false);
  readonly isRemovingSeat = signal(false);

  private readonly _searchTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private _roleInitialized = false;

  @ViewChild('roleSearchInput') roleSearchInput?: ElementRef<HTMLInputElement>;

  roleFormGroup!: FormGroup;

  constructor() {
    this._initRoleFormGroup();
    this._initUser();
  }

  private _initRoleFormGroup(): void {
    this.roleFormGroup = this._formBuilder.group({
      roleId: ['', Validators.required]
    });
  }

  private _initUser(): void {
    effect(() => {
      const user = this.facade.user();
      const isLoading = this.facade.isUserLoading();
      if (!isLoading && user.role_id && !this._roleInitialized) {
        this._roleInitialized = true;
        untracked(() => {
          this.roleFormGroup.patchValue({ roleId: user.role_id! });
          this.facade.setSelectedRoleId(user.role_id!);
          this._initScopesFromUser(user.permission_scopes ?? []);
        });
      }
    });
  }
  ngOnInit(): void {
    this._initUserId();
    this._initRoleIdChangeSubscription();
  }

  ngOnDestroy(): void {
    this._searchTimers.forEach(t => clearTimeout(t));
    this._searchTimers.clear();
  }

  private _initUserId(): void {
    const id = this._route.snapshot.paramMap.get('id');
    if (id) {
      this.facade.setUserId(id);
    } else {
      this._router.navigate(['/settings/users']);
    }
  }

  private _initRoleIdChangeSubscription(): void {
    this.roleFormGroup.controls['roleId'].valueChanges.subscribe((id: string) => {
      if (id && this._roleInitialized) {
        this.facade.setSelectedRoleId(id);
        this.permissionSelectedScopes.set({});
        this.permissionSearchTerms.set({});
        this.permissionSearchResults.set({});
      }
    });
  }

  async onSave(): Promise<void> {
    if (this.roleFormGroup.invalid) return;
    this.isSaving.set(true);
    try {
      const user = this.facade.user();
      const scopes = this._getScopes();
      await this.facade.updateUser({
        azure_user_id: user.intune_user_id ?? '',
        role_id: this.roleFormGroup.value.roleId,
        ...(Object.keys(scopes).length > 0 ? { scopes } : {}),
      });
      this._router.navigate(['/settings/users']);
    } finally {
      this.isSaving.set(false);
    }
  }

  onCancel(): void {
    this._router.navigate(['/settings/users']);
  }

  confirmRemoveSeat(): void {
    const dialogRef = this._msgService.showConfirm('app.settings.user_edit.remove_seat.dialog_title', 'app.settings.user_edit.remove_seat.confirm_text', '', 'person_remove', 'error');
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) this.executeRemoveSeat();
    });
  }

  async executeRemoveSeat(): Promise<void> {
    this.isRemovingSeat.set(true);
    try {
      await this.facade.removeSeat();
      this._router.navigate(['/settings/users']);
    } finally {
      this.isRemovingSeat.set(false);
    }
  }

  private _getScopes(): Record<string, ScopeItem[]> {
    const scopes: Record<string, ScopeItem[]> = {};
    Object.entries(this.permissionSelectedScopes()).forEach(([permId, entities]) => {
      if (entities.length > 0) scopes[permId] = entities.map(e => ({ uuid: e.uuid, type: e.type, displayName: e.displayName ?? e.uuid }));
    });
    return scopes;
  }

  private _initScopesFromUser(permissionScopes: PermissionScope[]): void {
    const initial: Record<number, ScopeItem[]> = {};
    for (const ps of permissionScopes) {
      if (ps.permission_id != null && ps.scope?.length) {
        initial[ps.permission_id] = ps.scope.map(s => ({ uuid: s.uuid ?? '', type: s.type ?? 'user', displayName: s.displayName ?? s.uuid ?? '', }));
      }
    }
    this.permissionSelectedScopes.set(initial);
  }

  onRoleSearchChange(event: Event): void {
    this._listRolesFacade.setSearchTerm((event.target as HTMLInputElement).value);
  }

  onRoleDropdownChange(opened: boolean): void {
    if (opened) {
      setTimeout(() => this.roleSearchInput?.nativeElement.focus(), 50);
    } else {
      this._listRolesFacade.setSearchTerm('');
    }
  }

  getPermissionSearchTerm(permissionId: number): string {
    return this.permissionSearchTerms()[permissionId] ?? '';
  }

  getPermissionSearchResults(permissionId: number): ScopeItem[] {
    return this.permissionSearchResults()[permissionId] ?? [];
  }

  getSelectedEntities(permissionId: number): ScopeItem[] {
    return this.permissionSelectedScopes()[permissionId] ?? [];
  }

  getEntityIcon(type: string): string {
    const icons: Record<string, string> = { user: 'person', group: 'group', device: 'computer' };
    return icons[type] ?? 'help_outline';
  }

  onPermissionSearch(permissionId: number, event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.permissionSearchTerms.update(m => ({ ...m, [permissionId]: term }));
    this._cancelPendingSearch(permissionId);
    if (term.length > 2) {
      this._scheduleSearch(permissionId, term);
    } else {
      this.permissionSearchResults.update(m => ({ ...m, [permissionId]: [] }));
    }
  }

  private _cancelPendingSearch(permissionId: number): void {
    const existing = this._searchTimers.get(permissionId);
    if (existing) clearTimeout(existing);
  }

  private _scheduleSearch(permissionId: number, term: string): void {
    this.permissionSearchLoading.update(m => ({ ...m, [permissionId]: true }));
    this._searchTimers.set(permissionId, setTimeout(() => this._executeSearch(permissionId, term), 300));
  }

  private async _executeSearch(permissionId: number, term: string): Promise<void> {
    try {
      const response: any = await firstValueFrom(
        this._apiService.run<any>(this._searchService.index(term, { users: '1', groups: '1', devices: '1' }))
      );
      const data = response?.data ?? {};
      this.permissionSearchResults.update(m => ({ ...m, [permissionId]: this._mapToScopeEntities(data) }));
    } finally {
      this.permissionSearchLoading.update(m => ({ ...m, [permissionId]: false }));
    }
  }

  onEntitySelected(permissionId: number, event: MatAutocompleteSelectedEvent): void {
    const entity = event.option.value as ScopeItem;
    const current = this.permissionSelectedScopes()[permissionId] ?? [];
    if (!current.find(e => e.uuid === entity.uuid)) {
      this.permissionSelectedScopes.update(m => ({ ...m, [permissionId]: [...current, entity] }));
    }
    this.permissionSearchTerms.update(m => ({ ...m, [permissionId]: '' }));
    this.permissionSearchResults.update(m => ({ ...m, [permissionId]: [] }));
  }

  removeEntity(permissionId: number, entity: ScopeItem): void {
    this.permissionSelectedScopes.update(m => ({
      ...m,
      [permissionId]: (m[permissionId] ?? []).filter(e => e.uuid !== entity.uuid),
    }));
  }

  private _mapToScopeEntities(data: { users?: any[]; groups?: any[] }): ScopeItem[] {
    const entities: ScopeItem[] = [];
    (data.users ?? []).forEach(u => entities.push({ uuid: u.id, type: 'user', displayName: u.displayName ?? u.userPrincipalName ?? u.id }));
    (data.groups ?? []).forEach(g => entities.push({ uuid: g.id, type: 'group', displayName: g.displayName ?? g.id }));
    return entities;
  }
}

